import { Component, OnInit } from '@angular/core';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import * as XLSX from 'xlsx';

export interface ClientData {
  clientId: number;
  nationalId: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
}

export interface ItemData {
  itemId: number;
  productName: string;
  variantValueName: string;
  shopPointId: number;
}

export interface OrderRow {
  cedula: string;
  item_id: string;
  payment_method: string;
  status: string;
}

export interface ValidOrder {
  orderData: OrderRow;
  client: ClientData;
  shopData: any;
  item: ItemData;
}

export interface ErrorReportRow {
  ItemId: string;
  Cedula: string;
  Mensaje: string;
}

@Component({
  selector: 'app-bulk-create-sale',
  templateUrl: './bulk-create-sale.component.html',
  styleUrls: ['./bulk-create-sale.component.scss']
})
export class BulkCreateSaleComponent implements OnInit {

  saleStateName: { [key: number]: string } = {
    0: 'Cancelado',
    1: 'Pendiente de verificación',
    2: 'Preparando orden',
    3: 'Entregado',
    4: 'Pendiente Compra Click',
    5: 'Completado',
    6: 'Parcial'
  };

  shopData: any;

  file: File | null = null;
  parsedOrders: OrderRow[] = [];
  validOrders: ValidOrder[] = [];   
  errorReportData: ErrorReportRow[] = []; 

  isProcessing = false;
  progress = 0;
  private progressInterval: any;
  isLoadingPreview = false;
  noValidData = false;
  processError: { label: string } | null = null;
  validationError: { label: string } | null = null;

  shopPointIdSelected = 0;

  paymentMethods: any[] = [];
  paymentTypes: any[] = [];

  constructor(
    private lambdaService: LambdaService,
    private themingService: ThemingService
  ) { }

  async ngOnInit(): Promise<void> {
    const response: any = await this.lambdaService.getPaymentMethods(this.themingService.shopId);
    const paymentTypes = Array.isArray(response?.paymentTypes) ? response.paymentTypes : [];
    const paymentMethods = Array.isArray(response?.paymentMethods) ? response.paymentMethods : [];
    this.paymentMethods = paymentMethods;
    this.paymentTypes = paymentTypes;
    console.log('Payment methods loaded:', this.paymentMethods);
    console.log('Payment types loaded:', this.paymentTypes);
    //filtrar unicamente los paymentTypes que estén en paymentMethods o que sea paymentTypeId == 15
    this.paymentTypes = this.paymentTypes.filter(pt => this.paymentMethods.some(pm => pm.paymentTypeId === pt.paymentTypeId) || pt.paymentTypeId === 15);
    // obtener el shoppoint de shopslocation que tenga shopPointDefault == 1
    const defaultShopPoint = this.themingService.shopTheming[0].shopsLocation.find((sp: any) => sp.shopPointDefault === 1);
    this.shopPointIdSelected = defaultShopPoint ? defaultShopPoint.shopPointId : 0;
    console.log('Default Shop Point ID Selected:', this.shopPointIdSelected);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      this.processExcelFile(this.file);
    }
  }

  getSaleStateName(state: string) {
    // saleStateName es un objeto que mapea los estados de la orden a sus nombres legibles
    return this.saleStateName[Number(state)] || 'Desconocido';
  }

  getPaymentTypeName(paymentMethodId: string) {
    const type = this.paymentTypes.find(pt => String(pt.paymentTypeId) === paymentMethodId);
    return type ? type.paymentTypeName : 'Desconocido';
  }

  async downloadTemplate() {
    const XLSX_ = XLSX;
    // Crear libro Excel
    const wb = XLSX_.utils.book_new();
    // Encabezados del archivo
    const headers = [
      'cedula',
      'item_id',
      'payment_method',
      'status',
    ];
    // Crear una fila de ejemplo (opcional)
    const sampleRow = {
      cedula: '102340567',
      item_id: '12345',
      payment_method: '15',
      status: '2'
    };
    // Convertir la fila en hoja
    const ws = XLSX_.utils.json_to_sheet([sampleRow], { header: headers });
    // Agregar la hoja al libro
    XLSX_.utils.book_append_sheet(wb, ws, 'cedulas');
    // Descargar archivo
    XLSX_.writeFile(wb, 'Plantilla_Ordenes.xlsx');
  }

  private async processExcelFile(file: File): Promise<void> {
    this.isLoadingPreview = true;
    this.parsedOrders = [];
    this.validOrders = [];
    this.errorReportData = [];
    this.noValidData = false;
    
    const reader = new FileReader();
    
    reader.onload = async (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawJson: any[] = XLSX.utils.sheet_to_json(worksheet);

        this.parsedOrders = rawJson.map((row: any) => ({
          cedula: String(row.cedula || '').trim(),
          item_id: String(row.item_id || '').trim(),
          payment_method: String(row.payment_method || '').trim(),
          status: String(row.status || '').trim()
        })).filter((order: OrderRow) => order.cedula.length > 0 && order.item_id.length > 0);

        if (this.parsedOrders.length === 0) {
          //alert('El archivo no contiene datos válidos.');
          this.validationError = {
            label: 'NO_VALID_DATA'
          };
          this.noValidData = true;
          this.isLoadingPreview = false;
          return;
        }

        console.log("Parsed Orders: ", this.parsedOrders);

        const uniqueCedulas = [...new Set(this.parsedOrders.map(o => o.cedula))];
        const uniqueItemIds = [...new Set(this.parsedOrders.map(o => Number(o.item_id)))];
        const shopId = this.themingService.shopTheming[0]?.shopId;

        const response: any = await this.lambdaService.validateBulkSalesData(shopId, uniqueCedulas, uniqueItemIds);

        // extraer solo .shopName .shopPrefix .shopDomain
        this.shopData = {
          shopPointId: this.shopPointIdSelected,
          shopName: this.themingService.shopTheming[0]?.shopName,
          shopPrefix: this.themingService.shopTheming[0]?.shopPrefix,
          shopDomain: this.themingService.shopTheming[0]?.shopDomain
        };

        console.log("Shop Data: ", this.shopData);

        if (response && response.success) {
          // Crear Maps para búsqueda rápida O(1)
          const clientsMap = new Map<string, ClientData>();
          response.validClients.forEach((client: ClientData) => {
            clientsMap.set(client.nationalId, client);
          });

          const itemsMap = new Map<number, ItemData>();
          response.validItems.forEach((item: ItemData) => {
            itemsMap.set(item.itemId, item);
          });

          // Clasificar filas
          this.parsedOrders.forEach(order => {
            const client = clientsMap.get(order.cedula);
            const item = itemsMap.get(Number(order.item_id));

            if (client && item) {
              // Fila válida: guardar con todos los datos
              this.validOrders.push({
                orderData: order,
                shopData: this.shopData,
                client: client,
                item: item
              });
            } else {
              // Fila con error
              const messages: string[] = [];
              if (!client) messages.push("Cliente no fue encontrado en el sistema");
              if (!item) messages.push("Este item no fue encontrado en el sistema");

              this.errorReportData.push({
                ItemId: order.item_id,
                Cedula: order.cedula,
                Mensaje: messages.join(" | ")
              });
            }
          });
        } else {
          throw new Error(response?.message || 'Validation failed');
        }

      } catch (error) {
        console.error('Error processing Excel file:', error);
        this.validationError = {
          label: 'VALIDATION_ERROR'
        };
      } finally {
        this.isLoadingPreview = false;
      }
    };
    
    reader.readAsArrayBuffer(file);
  }

  downloadErrorReport(): void {
    if (this.errorReportData.length === 0) {
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(this.errorReportData, {
      header: ['ItemId', 'Cedula', 'Mensaje']
    });

    const colWidths = [
      { wch: 30 },
      { wch: 30 },
      { wch: 60 }
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Errores');
    XLSX.writeFile(wb, 'Reporte_Errores_Importacion.xlsx');
  }

  async processBulk(): Promise<void> {
    console.log('Iniciando proceso bulk...');
    this.isProcessing = true;
    this.processError = null;
    if (this.validOrders.length === 0) {
      this.isProcessing = false;
      return;
    }

    // Ahora this.validOrders tiene toda la info necesaria:
    // - orderData: los datos originales del Excel
    // - client: datos completos del cliente
    // - item: datos completos del item (incluyendo shopPointId)
    
    console.log('Órdenes válidas para procesar:', this.validOrders);

    try {
      const response: any = await this.lambdaService.processBulkCreateSales(
        this.themingService.shopTheming[0].shopId,
        this.validOrders
      );
      
      console.log("RESPUESTA DE bulkAccountAccess", response);
      
      if (response && response.success) {
        // Iniciar la simulación de progreso basada en el tiempo estimado
        this.startProgressSimulation();
        // this.isProcessing = false;
      } else {
        this.processError = {
          label: 'PROCESS_ERROR'
        };
        this.isProcessing = false;
      }
    } catch (error) {
      console.error("Error en processBulk:", error);
      this.processError = {
        label: 'CONNECTION_ERROR'
      };
      this.isProcessing = false;
    }
    
    // Aquí llamarías a tu endpoint de creación de órdenes
    // await this.lambdaService.createBulkOrders(this.validOrders);
  }

  private startProgressSimulation(): void {
    const totalClients = this.validOrders.length;
    
    // 10 clientes = 6000 ms  =>  1 cliente = 600 ms
    const estimatedTimeMs = totalClients * 600; 
    
    // Actualizamos la barra cada 100 ms para que se vea fluida
    const updateIntervalMs = 100; 
    
    // Calculamos cuánto porcentaje sumar en cada intervalo de 100ms
    const progressIncrement = (updateIntervalMs / estimatedTimeMs) * 100;

    this.progressInterval = setInterval(() => {
      this.progress += progressIncrement;
      
      // Limitamos a 99% para dar la sensación de "Finalizando..." 
      // y evitar que se cierre si el backend se retrasa un poco más de lo estimado.
      // Cuando llegue a 99%, esperamos un poco y cerramos.
      if (this.progress >= 99) {
        this.progress = 99;
        clearInterval(this.progressInterval);
        
        setTimeout(() => {
          this.progress = 100;
          setTimeout(() => {
            this.isProcessing = false;
          }, 300);
        }, 1500);
      }
    }, updateIntervalMs);
  }

  private clearProgressInterval(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  // Buena práctica: limpiar el intervalo si el componente se destruye
  ngOnDestroy(): void {
    this.clearProgressInterval();
  }


}
